import { useEffect, useState } from 'react';
import { PageShell } from '../components/PageShell';
import { KpiCard } from '../components/financeiro/KpiCard';
import { fmtBRL } from '../components/financeiro/formatadores';
import { usePode, useCustosPainel, useCustosEventos, useCustosConfig } from '../hooks';
import { api } from '../api';
import type { CustosConfig } from '../types';

const ETAPA_LABEL: Record<string, string> = {
  mapa: 'Mapas', blocos: 'Peças (blocos)', triagem: 'Triagens', refino: 'Refinos', peca: 'Petições',
};
const etapaLbl = (e: string | null) => (e ? ETAPA_LABEL[e] ?? e : '—');
const reais = (c: number) => fmtBRL((c ?? 0) / 100);
const hhmm = (ts: string) => { try { return new Date(ts).toLocaleString('pt-BR'); } catch { return ts; } };
const nf = new Intl.NumberFormat('pt-BR');
const fmtUsd = (n: number) => `US$ ${(n ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}`;

export function Custos() {
  const pode = usePode();
  const cfgQ = useCustosConfig();
  const ehSuper = !cfgQ.isError && !!cfgQ.data;   // só o superadmin lê /config

  // Dono da plataforma começa na visão global (a assinatura é da plataforma);
  // se o usuário não puder ver o global, o efeito cai para o tenant.
  const [escopo, setEscopo] = useState<'global' | 'tenant'>('global');
  const painel = useCustosPainel(escopo);
  const eventosQ = useCustosEventos(escopo, 40);
  useEffect(() => {
    if (escopo === 'global' && painel.isError) setEscopo('tenant');
  }, [escopo, painel.isError]);

  if (!pode('aba.custos')) {
    return (
      <PageShell titulo={<h1 className="font-semibold text-gray-800 dark:text-gray-100">💸 Custos</h1>}>
        <p className="text-sm text-slate-500">Você não tem acesso a esta área.</p>
      </PageShell>
    );
  }

  const p = painel.data;
  const k = p?.kpis;
  const custoAssinatura = (p?.config.assinatura_custo_mensal_centavos ?? 0) / 100;

  const toggle = ehSuper ? (
    <div className="flex items-center rounded-lg overflow-hidden text-xs border border-slate-200 dark:border-slate-700">
      {(['global', 'tenant'] as const).map((e) => (
        <button key={e} onClick={() => setEscopo(e)}
          className={`px-2.5 py-1.5 whitespace-nowrap transition-colors ${escopo === e
            ? 'bg-blue-600 text-white font-semibold'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
          {e === 'global' ? '🌐 Plataforma' : '🏢 Este escritório'}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <PageShell
      subbarra={
        <>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">💸 Custos — assinatura</span>
            <span className="text-xs text-slate-400 hidden sm:inline">consumo de IA por chamada → R$</span>
          </div>
          {toggle}
        </>
      }
    >
      {!p ? (
        <div className="text-center text-sm text-slate-400 py-16">Carregando…</div>
      ) : (
        <div className="space-y-4">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard rotulo="% da assinatura (mês)"
              valor={`${k!.pct_assinatura.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
              tom={k!.pct_assinatura >= 90 ? 'vermelho' : k!.pct_assinatura >= 70 ? 'ambar' : 'azul'}
              progresso={k!.pct_assinatura / 100}
              sub={`${reais(k!.valor_centavos_total)} de ${fmtBRL(custoAssinatura)}`} />
            <KpiCard rotulo="R$ consumido (mês)" valor={reais(k!.valor_centavos_total)} tom="verde"
              sub="amortização da assinatura" />
            <KpiCard rotulo="Projeção do mês" valor={reais(k!.projecao_centavos_mes)}
              sub="extrapolando o ritmo atual" />
            <KpiCard rotulo="R$ por chamada" valor={reais(k!.valor_centavos_por_call)}
              sub={`${nf.format(k!.n_calls)} chamada(s)`} />
            <KpiCard rotulo="Chamadas de IA (mês)" valor={nf.format(k!.n_calls)}
              sub="mapa · blocos · triagem · refino" />
            <KpiCard rotulo="Tokens (mês)" valor={nf.format(k!.tokens_total)}
              sub="entrada + saída" />
            <KpiCard rotulo="Teto mensal de tokens" valor={nf.format(p.config.teto_mensal_tokens)}
              sub={p.config.restricao
                ? `limitado pelo reset ${p.config.restricao === 'semanal' ? 'semanal' : 'diário'}`
                : 'configure os tetos de tokens'} />
            <KpiCard rotulo="Equivalente-API" valor={fmtUsd(k!.custo_usd_total)}
              sub="o que custaria na API paga" />
          </div>

          {/* Consumo por etapa */}
          <div className="bg-white dark:bg-slate-900/40 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Consumo por etapa</div>
            {p.por_etapa.length === 0 ? (
              <p className="text-sm text-slate-400">Nenhuma chamada no período.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {p.por_etapa.map((e) => (
                  <div key={e.etapa ?? '—'} className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-3">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{etapaLbl(e.etapa)}</div>
                    <div className="text-lg font-semibold tabular-nums">{reais(e.valor_centavos)}</div>
                    <div className="text-[11px] text-slate-400">{nf.format(e.calls)} call(s) · {nf.format(e.tokens)} tok</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ranking por escritório (só global) */}
          {escopo === 'global' && p.ranking.length > 0 && (
            <div className="bg-white dark:bg-slate-900/40 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Consumo por escritório</div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <th className="py-1.5">Escritório</th><th className="text-right">Chamadas</th><th className="text-right">R$</th></tr></thead>
                <tbody>
                  {p.ranking.map((r) => (
                    <tr key={r.escritorio_id} className="border-b border-slate-100 dark:border-slate-800/60">
                      <td className="py-1.5">{r.nome}</td>
                      <td className="text-right tabular-nums">{nf.format(r.calls)}</td>
                      <td className="text-right tabular-nums">{reais(r.valor_centavos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Feed ao vivo */}
          <div className="bg-white dark:bg-slate-900/40 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Chamadas ao vivo</div>
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400">● atualiza a cada 10s</span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <th className="py-1.5">Quando</th><th>Etapa</th><th className="text-right">Tokens</th>
                  <th className="text-right">Equiv-API</th><th className="text-right">R$</th></tr></thead>
                <tbody>
                  {(eventosQ.data?.eventos ?? []).map((e) => (
                    <tr key={e.id} className="border-b border-slate-100 dark:border-slate-800/60">
                      <td className="py-1.5 text-slate-500 dark:text-slate-400 whitespace-nowrap">{hhmm(e.ts)}</td>
                      <td>{etapaLbl(e.etapa)}</td>
                      <td className="text-right tabular-nums">{nf.format((e.input_tokens ?? 0) + (e.output_tokens ?? 0))}</td>
                      <td className="text-right tabular-nums text-slate-400">{fmtUsd(e.custo_usd)}</td>
                      <td className="text-right tabular-nums">{e.valor_centavos ? reais(e.valor_centavos) : '—'}</td>
                    </tr>
                  ))}
                  {(eventosQ.data?.eventos ?? []).length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-slate-400">Nenhuma chamada registrada no período.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Config da assinatura (só superadmin) */}
          {ehSuper && cfgQ.data && (
            <ConfigAssinatura cfg={cfgQ.data} onMudou={() => { cfgQ.refetch(); painel.refetch(); }} />
          )}
        </div>
      )}
    </PageShell>
  );
}

function ConfigAssinatura({ cfg, onMudou }: { cfg: CustosConfig; onMudou: () => void }) {
  const [custo, setCusto] = useState(((parseFloat(cfg.assinatura_custo_mensal_centavos) || 0) / 100).toString());
  const [tokDia, setTokDia] = useState(String(cfg.assinatura_tokens_dia ?? ''));
  const [tokSem, setTokSem] = useState(String(cfg.assinatura_tokens_semana ?? ''));
  const [msg, setMsg] = useState<string | null>(null);
  const inp = 'px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm w-36';

  async function salvar() {
    setMsg(null);
    const c = parseFloat((custo || '').replace(',', '.'));
    const td = parseInt((tokDia || '').replace(/\D/g, ''), 10);
    const ts = parseInt((tokSem || '').replace(/\D/g, ''), 10);
    if (!(c >= 0) || !(td > 0) || !(ts > 0)) { setMsg('Informe custo mensal e os tetos de tokens (dia e semana).'); return; }
    try {
      await api.salvarCustosConfig({ assinatura_custo_mensal_reais: c, assinatura_tokens_dia: td, assinatura_tokens_semana: ts });
      setMsg('Salvo.'); onMudou();
    } catch { setMsg('Falha ao salvar.'); }
  }

  return (
    <div className="bg-white dark:bg-slate-900/40 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Assinatura (você informa) — superadmin</div>
      <p className="text-[11px] text-slate-400 mb-3">
        Teto mensal de tokens = menor entre (tokens/dia × dias do mês) e (tokens/semana × semanas do mês).
        R$ por chamada = (tokens da chamada ÷ teto mensal de tokens) × custo mensal. Calibre os tetos rodando
        <span className="font-mono"> /usage</span> no Claude Code (mostra o % da sessão de 5h e da semana).
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500 dark:text-slate-400">Custo mensal (R$)
          <input className={`${inp} block mt-1 w-28`} inputMode="decimal" value={custo} onChange={(e) => setCusto(e.target.value)} />
        </label>
        <label className="text-xs text-slate-500 dark:text-slate-400">Tokens por dia
          <input className={`${inp} block mt-1`} inputMode="numeric" placeholder="ex.: 2500000" value={tokDia} onChange={(e) => setTokDia(e.target.value)} />
        </label>
        <label className="text-xs text-slate-500 dark:text-slate-400">Tokens por semana
          <input className={`${inp} block mt-1`} inputMode="numeric" placeholder="ex.: 12000000" value={tokSem} onChange={(e) => setTokSem(e.target.value)} />
        </label>
        <button onClick={salvar} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Salvar</button>
        {msg && <span className="text-[11px] text-amber-600 dark:text-amber-400">{msg}</span>}
      </div>
    </div>
  );
}
